import { createApi } from "@reduxjs/toolkit/dist/query/react";
import axiosBaseQuery from "../axiosBaseQuery";
import { SummaryTemplateField } from "@callveriq/type/AiSummary";
import { PaginationType } from "@callveriq/type/Common";
import {
    GenerateDescriptionResponse,
    IGenerateDescriptionPayload,
} from "@callveriq/type/WebSocketNotification";

export const aiSummaryApiSlice = createApi({
    reducerPath: "aiSummaryApiSlice",
    baseQuery: axiosBaseQuery({
        transformResponse: (response) => response,
    }),
    tagTypes: ["AiSummaryTemplates"],
    endpoints: (builder) => ({
        getAiSummaryTemplate: builder.query<
            PaginationType<SummaryTemplateField>,
            {
                next?: string;
                isArchive?: boolean;
            }
        >({
            query: ({ next, isArchive }) => ({
                url: next || "gpt/summary/",
                method: "GET",
                params: { is_archived: isArchive ? "true" : "false" },
            }),
            providesTags: ["AiSummaryTemplates"],
        }),
        createAiSummaryTemplate: builder.mutation<
            SummaryTemplateField,
            SummaryTemplateField
        >({
            query: ({ ...payload }) => ({
                url: "gpt/summary/",
                method: "POST",
                body: payload,
            }),
            invalidatesTags: ["AiSummaryTemplates"],
        }),
        getAiSummaryTemplateById: builder.query<SummaryTemplateField, number>({
            query: (id) => ({
                url: `gpt/summary/${id}/`,
                method: "GET",
            }),
        }),

        updateAiSummaryTemplate: builder.mutation<
            SummaryTemplateField,
            Partial<SummaryTemplateField> & Pick<SummaryTemplateField, "id">
        >({
            query: ({ id, ...payload }) => ({
                url: `gpt/summary/${id}/`,
                method: "PATCH",
                body: payload,
            }),
            invalidatesTags: ["AiSummaryTemplates"],
            onQueryStarted: async (
                { id, ...payload },
                { queryFulfilled, dispatch }
            ) => {
                const patchResult = dispatch(
                    aiSummaryApiSlice.util.updateQueryData(
                        "getAiSummaryTemplate",
                        { next: undefined, isArchive: false },
                        (draft) => {
                            const template = draft.results.find(
                                (t) => t.id === id
                            );
                            if (template) {
                                Object.assign(template, payload);
                            }
                        }
                    )
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        deleteAiSummaryTemplate: builder.mutation<void, number>({
            query: (id) => ({
                url: `gpt/summary/${id}/`,
                method: "DELETE",
            }),
            invalidatesTags: ["AiSummaryTemplates"],
        }),
        archiveAiSummaryTemplate: builder.mutation<
            SummaryTemplateField,
            { is_archived: boolean; id: number; rule_is_applied?: boolean }
        >({
            query: ({ id, ...payload }) => ({
                url: `gpt/summary/${id}/`,
                method: "PATCH",
                body: payload,
            }),
            invalidatesTags: ["AiSummaryTemplates"],
        }),
        generateAiDescription: builder.mutation<
            GenerateDescriptionResponse["data"],
            IGenerateDescriptionPayload
        >({
            query: (payload) => ({
                url: "/gpt/suggest_ai/",
                method: "POST",
                body: payload,
            }),
            transformResponse: (response: GenerateDescriptionResponse) =>
                response.data,
        }),
    }),
});

export const {
    useGetAiSummaryTemplateQuery,
    useLazyGetAiSummaryTemplateQuery,
    useCreateAiSummaryTemplateMutation,
    useUpdateAiSummaryTemplateMutation,
    useDeleteAiSummaryTemplateMutation,
    useGetAiSummaryTemplateByIdQuery,
    useArchiveAiSummaryTemplateMutation,
    useGenerateAiDescriptionMutation,
} = aiSummaryApiSlice;
